import { Link } from 'react-router-dom';
import { SearchCheck } from 'lucide-react';
import ServiceStoryPage from '../../components/ServiceStoryPage';

const color = 'hsl(14 100% 57%)';

export const seo = {
  title: 'Software Audit & Consolidation',
  tagline: 'One week to see what you’re really paying for',
  metaDesc:
    'A one-week Software Audit & Consolidation for UK businesses — we map every tool and subscription, spot overlaps and waste, and hand you a plain-English blueprint of what to cancel, keep and replace.',
  slug: 'software-audit',
  whatWeDoItems: [
    { heading: 'Full tool & subscription inventory', body: 'Every piece of software your business pays for, listed in one place — with what it does, who uses it, and what it costs you each month.' },
    { heading: 'Overlaps and waste identified', body: 'We pinpoint the tools doing the same job twice, the seats nobody logs into, and the subscriptions that quietly renewed themselves years ago.' },
    { heading: 'Plain-English Consolidation Blueprint', body: 'A clear cancel / keep / replace recommendation for every tool — written in normal language, not vendor jargon, so anyone in your business can follow it.' },
    { heading: 'Monthly saving, quantified', body: 'A real number — what you’re wasting today and what you’d save each month after consolidation. No fluff, no "up to" figures.' },
    { heading: 'One clear setup, mapped out', body: 'What your business would look like on one connected setup instead of ten disconnected ones — how the tools talk to each other and where data flows.' },
    { heading: 'Fixed quote to do the work', body: 'A fixed, no-obligation quote for us to run the consolidation for you and manage the result afterwards. You’re free to take the blueprint and walk away.' },
  ],
};

const faqs = [
  {
    q: 'Do we need your passwords?',
    a: 'No. We work from your bills, a short walkthrough call, and read-only access to anything you’re happy to share. If you want to give us admin access to speed things up you can, but it’s not required — we’ve done audits entirely from invoices and a screen-share.',
  },
  {
    q: 'How long does it take?',
    a: 'One week end-to-end. A 30-minute call to kick off, a few days for us to review your tools and bills, then we send the Blueprint. Most of that time is us working in the background — the demand on you is roughly an hour in total.',
  },
  {
    q: 'What if you find nothing worth cutting?',
    a: 'It’s rare, but it happens. If the audit doesn’t identify at least the cost of the audit in monthly savings or clear consolidation wins, we’ll tell you plainly and refund the fee. You keep the inventory and the Blueprint either way.',
  },
];

export default function SoftwareAuditPage() {
  return (
    <ServiceStoryPage
      tagline={seo.tagline}
      Icon={SearchCheck}
      color={color}
      ctaText="Book a free call"
      heroHeadline="Find out what you’re really paying for"
      heroSub="Most established businesses pay for tools they barely use, that don’t talk to each other. In one week we map the lot and show you what to cancel, what to keep, and what one clear setup would look like."
      problem={{
        heading: 'Subscriptions everywhere, nobody quite sure why',
        body: [
          'Over the years the tools just accumulated — one for invoicing, one for quotes, another for scheduling, three for messaging. Nobody remembers who signed up for half of them, and the monthly bills quietly keep coming out of the bank.',
          'You’ve got five tools doing two jobs, data being typed in twice, and nothing that actually talks to each other. It works — just — but it’s expensive, fiddly, and getting worse the bigger you grow.',
        ],
        footer: (
          <>
            Starting from scratch instead?{' '}
            <Link to="/services/vision-to-launch" className="underline" style={{ color }}>
              See Vision to Launch
            </Link>
            .
          </>
        ),
      }}
      whatYouGetIntro="A one-week deep-dive into every tool, subscription and bill — and a plain-English plan for what to do about it."
      whatYouGet={seo.whatWeDoItems}
      howItWorks={[
        { title: '30-minute call', body: 'A relaxed kick-off call to understand your business, the tools you rely on, and where the pain points are.' },
        { title: 'We review your tools & bills', body: 'You share your subscriptions, invoices and a quick walkthrough. We do the digging — no long questionnaires for you to fill in.' },
        { title: 'Blueprint delivered in one week', body: 'You get the full inventory, the overlaps and waste, the cancel/keep/replace plan, and the quantified monthly saving.' },
        { title: 'You decide what happens next', body: 'Take the Blueprint and do it yourself, hand it to your team, or have us run the consolidation for you on a fixed quote. No obligation either way.' },
      ]}
      proofHeading="Real results, real business"
      proofSub="Eclipse Paving started with tools scattered across five platforms — we consolidated them into one connected setup they now run day-to-day."
      pricing={{
        price: '£495 fixed',
        caption: 'Most audits find more monthly waste than the audit costs.',
      }}
      faqs={faqs}
      finalHeading="Ready to see what you’re really paying for?"
      finalSub="Book a free call and we’ll walk through how the audit works — one week from now you’ll know exactly what to cancel, keep and replace."
    />
  );
}
